import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useState } from 'react';
import { toast } from 'sonner';
import { psychologistsApi } from '@/api/psychologists';
import { useAuth, useSyncAuthUser } from '@/context/AuthContext';
import { READINESS_CHECKLIST_QUERY_KEY } from '@/hooks/use-readiness-checklist';
import { profileSaveErrorMessage } from '../profile-fields';
import type { PsychologistUpdateData } from '@/types/psychologist';

/**
 * The single write path for the profile fields the customize modal edits inline
 * (nome, CRP, telefone, Instagram).
 *
 * Unlike everything else in the modal these are account-level values: they save
 * on confirm rather than joining the Aplicar/Cancelar buffer, so Cancelar never
 * rolls one back.
 *
 * Three caches have to move on every save:
 *  - `['auth-bootstrap']` via `useSyncAuthUser` — `staleTime: Infinity`, and it
 *    is what the cabeçalho, rodapé, assinatura, capa and this modal's own
 *    preview all render from. Skipping it leaves the old value on screen.
 *  - `['psychologist']` — the Perfil page's own query.
 *  - the readiness checklist — the API derives `profile_completed` from
 *    `full_name && crp`.
 */
export function useProfileFieldUpdate() {
    const { user } = useAuth();
    const syncAuthUser = useSyncAuthUser();
    const queryClient = useQueryClient();
    const [isPending, setIsPending] = useState(false);

    const save = useCallback(
        async (patch: PsychologistUpdateData, successMessage: string): Promise<boolean> => {
            if (!user?.id) return false;

            setIsPending(true);
            try {
                const updated = await psychologistsApi.update(user.id, patch);
                await syncAuthUser(updated);
                void queryClient.invalidateQueries({ queryKey: ['psychologist'] });
                void queryClient.invalidateQueries({ queryKey: READINESS_CHECKLIST_QUERY_KEY });
                toast.success(successMessage);
                return true;
            } catch (error) {
                toast.error(profileSaveErrorMessage(error));
                return false;
            } finally {
                setIsPending(false);
            }
        },
        [user?.id, syncAuthUser, queryClient],
    );

    return { save, isPending };
}
